// スタンダード(SaaS 風 LP)テンプレート。
// 変数は fields で宣言し、body はその値(v)を data-bind / data-img 付きで描く。

import {
  type BuildOpts,
  type Field,
  type TemplateDef,
  type Values,
  formatText,
  imageSlot,
  seg,
  svg,
} from "./shared";

// 特徴カードのアイコン(静的)。タイトル・本文は編集可能な変数にする。
const FEATURE_ICONS = [
  `<path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"></path>`,
  `<rect x="3" y="3" width="18" height="18" rx="2"></rect><path d="M3 9h18M9 21V9"></path>`,
  `<path d="M3 3v18h18"></path><path d="M7 14l4-4 3 3 5-6"></path>`,
];

const fields: Field[] = [
  // カラー
  { key: "brandColor", label: "ブランドカラー", group: "カラー", kind: "color", cssVar: "--brand", onVar: "--on-brand", default: "#2563eb" },
  { key: "accentColor", label: "アクセントカラー", group: "カラー", kind: "color", cssVar: "--accent", onVar: "--on-accent", default: "#f59e0b" },
  { key: "baseColor", label: "ベース背景色", group: "カラー", kind: "color", cssVar: "--base", onVar: "--on-base", default: "#ffffff" },
  // タイポグラフィ
  { key: "font", label: "フォント", group: "タイポグラフィ", kind: "font", cssVar: "--font", default: "gothic" },
  // コンテンツ
  { key: "badge", label: "バッジ文言", group: "コンテンツ", kind: "text", format: "plain", default: "誰でも、たった5分で公開" },
  { key: "headline", label: "見出し", group: "コンテンツ", kind: "text", format: "headline", default: "あなたのビジネスを**次のステージへ**" },
  { key: "subhead", label: "サブ見出し", group: "コンテンツ", kind: "textarea", format: "inline", default: "シンプルで美しいランディングページを、今すぐ。" },
  { key: "ctaLabel", label: "CTA ボタン文言", group: "コンテンツ", kind: "text", format: "plain", default: "無料で始める" },
  { key: "featuresTitle", label: "特徴セクション見出し", group: "コンテンツ", kind: "text", format: "inline", default: "選ばれる3つの理由" },
  { key: "featuresSub", label: "特徴セクション説明", group: "コンテンツ", kind: "text", format: "inline", default: "制作から公開、改善まで。必要なものはすべて揃っています。" },
  { key: "feat1Title", label: "特徴1 タイトル", group: "コンテンツ", kind: "text", format: "inline", default: "かんたん3分で公開" },
  { key: "feat1Body", label: "特徴1 本文", group: "コンテンツ", kind: "textarea", format: "inline", default: "直感的なエディターでドラッグ&ドロップ。専門知識がなくても、洗練されたページがすぐに完成します。" },
  { key: "feat2Title", label: "特徴2 タイトル", group: "コンテンツ", kind: "text", format: "inline", default: "美しいデザイン" },
  { key: "feat2Body", label: "特徴2 本文", group: "コンテンツ", kind: "textarea", format: "inline", default: "プロがつくったテンプレートを多数用意。どんなデバイスでも崩れない、レスポンシブ対応です。" },
  { key: "feat3Title", label: "特徴3 タイトル", group: "コンテンツ", kind: "text", format: "inline", default: "データで改善" },
  { key: "feat3Body", label: "特徴3 本文", group: "コンテンツ", kind: "textarea", format: "inline", default: "アクセス解析とA/Bテストを標準搭載。数字を見ながら、成果につながるページへ育てられます。" },
  { key: "bandTitle", label: "CTAバンド見出し", group: "コンテンツ", kind: "text", format: "inline", default: "今すぐ、あなたのページを始めましょう" },
  { key: "bandSub", label: "CTAバンド説明", group: "コンテンツ", kind: "text", format: "inline", default: "登録は30秒。無料プランでずっとお使いいただけます。" },
  // 画像
  { key: "heroShot", label: "ヒーロー画像", group: "画像", kind: "image", placeholder: "ヒーロー画像" },
];

const css = `
.wrap{max-width:1120px;margin:0 auto;padding-left:28px;padding-right:28px}

/* Header */
.hdr{position:sticky;top:0;z-index:50;background:color-mix(in srgb, var(--base) 85%, transparent);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border-bottom:1px solid var(--line)}
.hdr-in{display:flex;justify-content:space-between;align-items:center;gap:20px;padding-top:18px;padding-bottom:18px;overflow-x:auto;scrollbar-width:none}
.hdr-in::-webkit-scrollbar{display:none}
.brand{display:flex;align-items:center;gap:11px;flex-shrink:0}
.brand-mark{width:30px;height:30px;border-radius:9px;background:var(--brand);display:flex;align-items:center;justify-content:center}
.brand-dot{width:12px;height:12px;border-radius:3px;background:var(--on-brand)}
.brand-name{font-weight:900;font-size:19px;letter-spacing:-.02em;color:var(--on-base)}
.nav{display:flex;align-items:center;gap:34px;flex-wrap:nowrap;flex-shrink:0}
.nav a{color:var(--muted);text-decoration:none;font-size:14.5px;font-weight:500;white-space:nowrap}
.nav a.nav-cta{background:var(--brand);color:var(--on-brand);font-size:14px;font-weight:700;padding:10px 20px;border-radius:9px}

/* Hero */
.hero{position:relative;overflow:hidden;background:var(--base)}
.hero-glow{position:absolute;top:-180px;right:-140px;width:520px;height:520px;border-radius:50%;background:radial-gradient(circle, color-mix(in srgb, var(--brand) 10%, transparent), transparent 70%);pointer-events:none}
.hero-in{position:relative;text-align:center;padding-top:110px;padding-bottom:96px}
.badge{display:inline-flex;align-items:center;gap:8px;background:var(--brand-soft);border:1px solid var(--brand-line);color:var(--brand);font-size:13px;font-weight:700;padding:7px 15px;border-radius:999px;margin-bottom:30px}
.badge-dot{width:7px;height:7px;border-radius:50%;background:var(--accent)}
.hero-title{font-size:56px;line-height:1.28;font-weight:900;letter-spacing:-.03em;margin:0 0 22px;color:var(--on-base);text-wrap:balance}
.hero-sub{font-size:20px;color:var(--muted);margin:0 auto 40px;max-width:560px;font-weight:500}
.hero-cta{display:flex;gap:14px;justify-content:center;align-items:center;flex-wrap:wrap}
.hero-note{font-size:13px;color:var(--faint);margin:26px 0 0;font-weight:500}
.btn{display:inline-flex;align-items:center;gap:9px;text-decoration:none;font-weight:700;border-radius:11px}
.btn-arrow{font-size:18px}
.btn-primary{background:var(--accent);color:var(--on-accent);padding:16px 34px;font-size:16.5px;box-shadow:0 10px 24px -8px color-mix(in srgb, var(--accent) 55%, transparent)}
.btn-ghost{background:var(--base);color:var(--on-base);padding:16px 28px;font-size:16px;border:1px solid color-mix(in srgb, var(--on-base) 12%, var(--base))}

/* Browser mock */
.mock{margin-top:64px;background:var(--base);border:1px solid var(--card-line);border-radius:18px;box-shadow:0 40px 80px -30px rgba(15,23,42,.22);overflow:hidden;text-align:left}
.mock-bar{display:flex;align-items:center;gap:8px;padding:14px 18px;border-bottom:1px solid var(--line);background:var(--surface)}
.mock-bar .dot{width:11px;height:11px;border-radius:50%}
.dot-r{background:#f87171}.dot-y{background:#fbbf24}.dot-g{background:#34d399}
.mock-url{margin-left:14px;font-size:12px;color:var(--faint);background:color-mix(in srgb, var(--on-base) 6%, var(--base));padding:4px 14px;border-radius:6px}
.mock-body{display:grid;grid-template-columns:1.2fr 1fr}
.mock-left{padding:48px 44px}
.ln{border-radius:5px}
.ln-accent{width:64px;height:8px;border-radius:4px;background:var(--accent);margin-bottom:20px}
.ln-dark{height:20px;background:var(--on-base);margin-bottom:12px}
.ln-brand{height:20px;background:var(--brand);margin-bottom:24px}
.ln-gray{height:10px;border-radius:3px;background:color-mix(in srgb, var(--on-base) 12%, var(--base));margin-bottom:9px}
.ln-gap{margin-bottom:28px}
.mock-btn{width:150px;height:42px;border-radius:9px;background:var(--brand)}
.mock-right{border-left:1px solid var(--card-line)}
.mock-right .img-ph{border:none}

/* Features */
.features{background:var(--surface);border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.sec-pad{padding-top:96px;padding-bottom:96px}
.sec-head{text-align:center;margin-bottom:60px}
.eyebrow{color:var(--brand);font-size:14px;font-weight:800;letter-spacing:.08em;margin-bottom:14px}
.sec-title{font-size:38px;font-weight:900;letter-spacing:-.025em;margin:0 0 14px;color:var(--on-base)}
.sec-sub{font-size:17px;color:var(--muted);margin:0;font-weight:500}
.cards{display:grid;grid-template-columns:repeat(3,1fr);gap:26px}
.card{background:var(--base);border:1px solid var(--card-line);border-radius:16px;padding:36px 30px}
.card-ico{width:52px;height:52px;border-radius:13px;background:var(--brand-soft);color:var(--brand);display:flex;align-items:center;justify-content:center;margin-bottom:22px}
.card h3{font-size:20px;font-weight:800;margin:0 0 12px;color:var(--on-base)}
.card p{font-size:15px;color:var(--muted);margin:0;font-weight:500}

/* CTA band */
.band{background:var(--brand);position:relative;overflow:hidden}
.band-glow{position:absolute;bottom:-160px;right:-60px;width:440px;height:440px;border-radius:50%;background:radial-gradient(circle, color-mix(in srgb, var(--on-brand) 12%, transparent), transparent 65%);pointer-events:none}
.band-in{position:relative;text-align:center;padding-top:88px;padding-bottom:88px}
.band-title{font-size:38px;font-weight:900;letter-spacing:-.025em;color:var(--on-brand);margin:0 0 16px;text-wrap:balance}
.band-sub{font-size:18px;color:color-mix(in srgb, var(--on-brand) 82%, transparent);margin:0 0 38px;font-weight:500}
.btn-accent{background:var(--accent);color:var(--on-accent);padding:17px 40px;border-radius:12px;font-weight:800;font-size:17px;box-shadow:0 12px 30px -8px rgba(0,0,0,.35)}

/* Footer */
.ft{background:#0f172a;color:#94a3b8}
.ft-pad{padding-top:56px;padding-bottom:40px}
.ft-top{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:32px;padding-bottom:36px;border-bottom:1px solid #1e293b}
.ft-brand{max-width:300px}
.ft-brand .brand{margin-bottom:16px}
.ft .brand-mark{width:28px;height:28px;border-radius:8px}
.ft .brand-dot{width:11px;height:11px}
.ft-name{font-weight:900;font-size:18px;color:#fff}
.ft-desc{font-size:14px;margin:0;line-height:1.7}
.ft-cols{display:flex;gap:64px;flex-wrap:wrap}
.ft-h{color:#fff;font-weight:700;font-size:14px;margin-bottom:16px}
.ft-links{display:flex;flex-direction:column;gap:11px}
.ft-links a{color:#94a3b8;text-decoration:none;font-size:14px}
.ft-copy{padding-top:26px;font-size:13px}

@media(max-width:860px){
  .cards{grid-template-columns:1fr}
  .mock-body{grid-template-columns:1fr}
  .mock-right{min-height:200px}
  .hero-in{padding-top:72px;padding-bottom:64px}
  .hero-title{font-size:38px}
  .sec-title,.band-title{font-size:30px}
  .sec-pad{padding-top:72px;padding-bottom:72px}
  .nav{gap:18px}
}`;

function body(v: Values, opts: BuildOpts): string {
  const cards = FEATURE_ICONS.map((icon, i) => {
    const n = i + 1;
    return `<div class="card">
        <div class="card-ico">${svg(icon)}</div>
        <h3 class="bdx" data-bind="feat${n}Title">${formatText(v[`feat${n}Title`] ?? "", "inline")}</h3>
        <p class="bdx" data-bind="feat${n}Body">${formatText(v[`feat${n}Body`] ?? "", "inline")}</p>
      </div>`;
  }).join("");

  // ボタン文言は data-bind の内側 span に置き、矢印(→)は兄弟にして
  // textContent 差し替えでも矢印が消えないようにする。
  const cta = (cls: string) =>
    `<a href="#cta" class="btn ${cls}"><span data-bind="ctaLabel">${formatText(v.ctaLabel ?? "", "plain")}</span><span class="btn-arrow">→</span></a>`;

  const heroShot = imageSlot({
    key: "heroShot",
    placeholder: "ヒーロー画像",
    className: "mock-right",
    url: v.heroShot,
  });

  return `
<!-- Header -->
<header class="hdr"><div class="wrap hdr-in">
  <div class="brand"><span class="brand-mark"><span class="brand-dot"></span></span><span class="brand-name">LP Design</span></div>
  <nav class="nav">
    <a href="#features">機能</a>
    <a href="#pricing">料金</a>
    <a href="#company">会社概要</a>
    <a href="#cta" class="nav-cta">ログイン</a>
  </nav>
</div></header>

<!-- Hero -->
<section class="hero">
  <div class="hero-glow"></div>
  <div class="wrap hero-in">
    <div class="badge"><span class="badge-dot"></span><span data-bind="badge">${formatText(v.badge ?? "", "plain")}</span></div>
    <h1 class="hero-title bdx" data-bind="headline">${formatText(v.headline ?? "", "headline")}</h1>
    <p class="hero-sub bdx" data-bind="subhead">${formatText(v.subhead ?? "", "inline")}</p>
    <div class="hero-cta">
      ${cta("btn-primary")}
      <a href="#features" class="btn btn-ghost">機能を見る</a>
    </div>
    <p class="hero-note">クレジットカード不要 ・ いつでも解約可能</p>

    <!-- Browser mock -->
    <div class="mock">
      <div class="mock-bar">
        <span class="dot dot-r"></span><span class="dot dot-y"></span><span class="dot dot-g"></span>
        <span class="mock-url">lpdesign.app/your-page</span>
      </div>
      <div class="mock-body">
        <div class="mock-left">
          <div class="ln ln-accent"></div>
          <div class="ln ln-dark" style="width:88%"></div>
          <div class="ln ln-brand" style="width:64%"></div>
          <div class="ln ln-gray" style="width:100%"></div>
          <div class="ln ln-gray" style="width:92%"></div>
          <div class="ln ln-gray ln-gap" style="width:70%"></div>
          <div class="mock-btn"></div>
        </div>
        ${heroShot}
      </div>
    </div>
  </div>
</section>

<!-- Features -->
<section id="features" class="features">
  <div class="wrap sec-pad">
    <div class="sec-head">
      <div class="eyebrow">FEATURES</div>
      <h2 class="sec-title bdx" data-bind="featuresTitle">${formatText(v.featuresTitle ?? "", "inline")}</h2>
      <p class="sec-sub bdx" data-bind="featuresSub">${formatText(v.featuresSub ?? "", "inline")}</p>
    </div>
    <div class="cards">
      ${cards}
    </div>
  </div>
</section>

<!-- CTA band -->
<section id="cta" class="band">
  <div class="band-glow"></div>
  <div class="wrap band-in">
    <h2 class="band-title bdx" data-bind="bandTitle">${formatText(v.bandTitle ?? "", "inline")}</h2>
    <p class="band-sub bdx" data-bind="bandSub">${formatText(v.bandSub ?? "", "inline")}</p>
    ${cta("btn-accent")}
  </div>
</section>

<!-- Footer -->
<footer class="ft">
  <div class="wrap ft-pad">
    <div class="ft-top">
      <div class="ft-brand">
        <div class="brand"><span class="brand-mark"><span class="brand-dot"></span></span><span class="ft-name">LP Design</span></div>
        <p class="ft-desc bdx">${seg("シンプルで美しいランディングページを、誰でも、今すぐ。")}</p>
      </div>
      <div class="ft-cols">
        <div class="ft-col">
          <div class="ft-h">製品</div>
          <div class="ft-links"><a href="#features">機能</a><a href="#pricing">料金</a><a href="#">テンプレート</a></div>
        </div>
        <div class="ft-col">
          <div class="ft-h">会社</div>
          <div class="ft-links"><a href="#company">会社概要</a><a href="#">お問い合わせ</a><a href="#">採用情報</a></div>
        </div>
      </div>
    </div>
    <div class="ft-copy">© 2026 LP Design, Inc. All rights reserved.</div>
  </div>
</footer>`;
}

export const standardTemplate: TemplateDef = {
  id: "standard",
  label: "スタンダード",
  fields,
  css,
  body,
};
